import * as React from 'react';

import { Actions } from '../Actions';
import { Description } from '../Description';
import { Group } from '../Group';
import { Heading } from '../Heading';
import { LoadingPanel } from '../Loading';
import { PanelSet } from '../Panel';

export interface PagePanelsProps {
    /**
     * Title of the page.
     */
    title: React.ReactNode;

    /**
     * Description of the page.
     */
    description?: React.ReactNode;

    /**
     * Action button to display.
     */
    action?: React.ReactNode;

    /**
     * Navigation breadcrumb.
     */
    breadcrumb?: React.ReactNode;
    /**
     * Panels to display.
     */
    children: React.ReactNode;
}

/**
 * Layout of a page containing multiple panels.
 */
export function PagePanels(props: PagePanelsProps) {
    const { children, ...headerProps } = props;

    return (
        <>
            <PagePanelsHeader {...headerProps} />
            <PagePanelsBody>{children}</PagePanelsBody>
        </>
    );
}

export function PagePanelsHeader(props: Omit<PagePanelsProps, 'children'>) {
    const { action, breadcrumb, description, title } = props;
    return (
        <div className="flex flex-col gap-4">
            {breadcrumb}
            <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-start">
                <Group className="flex-1">
                    <Heading className="text-ui-heading-large">{title}</Heading>
                    {description ? <Description>{description}</Description> : null}
                </Group>
                {action ? <Actions>{action}</Actions> : null}
            </div>
        </div>
    );
}

export function PagePanelsBody(props: Pick<PagePanelsProps, 'children'>) {
    return (
        <PanelSet>
            <React.Suspense fallback={<LoadingPanel size="xlarge" />}>
                {props.children}
            </React.Suspense>
        </PanelSet>
    );
}
